import React, { useEffect, useRef } from 'react';
import type { Landmark } from '@/components/posture-analysis/types';
import { containBox, skeletonJoints, skeletonSegments } from './lib/skeleton';

const VISIBILITY_THRESHOLD = 0.5;

/**
 * Full-body live landmarks over VGon's media pane (Goniometer mode). Fills its positioned parent,
 * ignores pointer events (goniometer arms stay draggable), and follows the video's displayed box;
 * when VGon freezes a frame (video hidden) it uses the contain-fit of the frame in the pane.
 */
export default function SkeletonOverlay({ video, landmarks }: { video: HTMLVideoElement | null; landmarks: Landmark[] | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const landmarksRef = useRef(landmarks);
  landmarksRef.current = landmarks;

  useEffect(() => {
    let frame = 0;
    const draw = () => {
      frame = requestAnimationFrame(draw);
      const canvas = canvasRef.current;
      const pane = canvas?.parentElement;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !pane || !ctx) return;
      const paneRect = pane.getBoundingClientRect();
      if (canvas.width !== Math.round(paneRect.width) || canvas.height !== Math.round(paneRect.height)) {
        canvas.width = Math.round(paneRect.width);
        canvas.height = Math.round(paneRect.height);
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const lms = landmarksRef.current;
      if (!lms || !video || !video.videoWidth || !video.videoHeight) return;

      let box;
      if (video.offsetWidth > 0 && video.offsetHeight > 0) {
        const r = video.getBoundingClientRect();
        box = { x: r.left - paneRect.left, y: r.top - paneRect.top, width: r.width, height: r.height };
      } else {
        box = containBox(video.videoWidth, video.videoHeight, paneRect.width, paneRect.height);
      }
      const sx = box.width / video.videoWidth;
      const sy = box.height / video.videoHeight;
      const px = (lm: Landmark) => [box.x + lm.x * sx, box.y + lm.y * sy] as const;

      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(34, 211, 238, 0.9)';
      for (const [a, b] of skeletonSegments(lms, VISIBILITY_THRESHOLD)) {
        const [ax, ay] = px(a);
        const [bx, by] = px(b);
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
        ctx.stroke();
      }
      ctx.fillStyle = 'rgba(250, 204, 21, 0.95)';
      for (const lm of skeletonJoints(lms, VISIBILITY_THRESHOLD)) {
        const [x, y] = px(lm);
        ctx.beginPath();
        ctx.arc(x, y, 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [video]);

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" style={{ pointerEvents: 'none' }} />;
}
